// src/utils/defaultSizes.js
//
// De id-afspraak voor Model.defaultSizes (TPLA-640, taak 8e).
//
// De API schrijft defaultSizes sinds taak 8e niet meer als HELE ARRAY terug maar
// per veld. Dat kan alleen als elk element herkenbaar is, dus draagt elk element
// op alle vier de niveaus -- defaultSizes, wall.height, breedtes en
// breedtes.wallDivision -- het `id` dat de query teruggeeft. Daaruit volgen drie
// regels waar het CMS zich aan moet houden:
//
//   1. Een BESTAAND element stuurt zijn id ongewijzigd terug. De query vraagt
//      `id` op alle vier de niveaus; de mutatie stuurt hem mee.
//   2. Een NIEUW element laat `id` weg. Een gekopieerd element is dus pas nieuw
//      als de id's uit de kopie gehaald zijn -- anders is het volgens de API
//      hetzelfde element als het origineel. Zie zonderIds().
//   3. VERWIJDEREN gebeurt op een expliciete `_delete: true` met het id erbij.
//      Een element uit de lijst weglaten verwijdert sinds taak 8e niets meer:
//      ontbreken betekent "hierover is niets gezegd". Zie metVerwijderingen().
//
// De API weigert luid (en schrijft dan niets) zodra de payload een element
// zonder id bevat terwijl er op datzelfde niveau een opgeslagen element niet bij
// zijn id genoemd wordt -- dat naamloze element zou dat ongenoemde element
// kunnen zijn. Zie tools/defaultSizesConfig.js in tw-configurator-api.

// De geneste lijsten binnen een defaultSize, met hun pad. Dezelfde boom als
// GUARD_CHILDREN in de API, zodat een nieuw niveau hier niet stilzwijgend
// ontbreekt.
const NIVEAUS = [
  { pad: ['wall', 'height'], kinderen: [] },
  { pad: ['breedtes'], kinderen: [{ pad: ['wallDivision'], kinderen: [] }] }
]

function lees (bron, pad) {
  return pad.reduce((acc, seg) => (acc === null || acc === undefined ? undefined : acc[seg]), bron)
}

// Schrijft op een genest pad zonder de tussenliggende objecten van de bron te
// muteren: elk niveau wordt eerst gekopieerd.
function schrijf (doel, pad, waarde) {
  let cur = doel
  for (let i = 0; i < pad.length - 1; i++) {
    const seg = pad[i]
    cur[seg] = (cur[seg] !== null && typeof cur[seg] === 'object' && !Array.isArray(cur[seg])) ? { ...cur[seg] } : {}
    cur = cur[seg]
  }
  cur[pad[pad.length - 1]] = waarde
}

/**
 * Haalt elk `id` uit een waarde weg, hoe diep ook. Een defaultSize kent geen
 * ander veld dat `id` heet dan de subdocument-id's zelf, dus dit is veilig voor
 * een hele maat, een losse breedte of een los wandvak.
 *
 * Gebruik dit op elke KOPIE: een kopie mét id is voor de API geen nieuw element
 * maar hetzelfde element, en dan schrijft de opslag de kopie boven op het
 * origineel in plaats van er een rij bij te zetten.
 *
 * Muteert de invoer niet.
 */
export function zonderIds (waarde) {
  if (Array.isArray(waarde)) return waarde.map(zonderIds)
  if (waarde === null || typeof waarde !== 'object') return waarde

  const uit = {}
  for (const sleutel of Object.keys(waarde)) {
    if (sleutel === 'id') continue
    uit[sleutel] = zonderIds(waarde[sleutel])
  }
  return uit
}

/**
 * Zet de muurhoogtes van alle maten gelijk aan die van de eerste maat.
 *
 * Het CMS laat wall.height maar op een plek bewerken (de eerste maat) en
 * spiegelt die naar de rest. Dat mag geen ARRAY-toewijzing meer zijn: dan kregen
 * alle maten de id's van maat 0, terwijl elke maat zijn eigen height-rijen met
 * eigen id's heeft. De API zou die vreemde id's niet terugvinden en er stil
 * niets mee doen.
 *
 * Daarom spiegelt deze functie op `position`: elke maat houdt zijn eigen id en
 * krijgt alleen de waarde overgenomen. Een positie die een maat nog niet kent
 * komt er zonder id bij en is dus een echt nieuwe rij.
 *
 * Muteert de meegegeven maten (ze komen uit een verse deep copy van de payload).
 */
export function spiegelMuurhoogtes (maten) {
  if (!Array.isArray(maten) || maten.length === 0) return maten

  const bron = lees(maten[0], ['wall', 'height'])
  if (!Array.isArray(bron)) return maten

  for (let i = 1; i < maten.length; i++) {
    const eigen = lees(maten[i], ['wall', 'height'])
    const eigenPerPositie = new Map()
    for (const rij of Array.isArray(eigen) ? eigen : []) {
      if (rij && rij.position !== undefined && rij.position !== null) eigenPerPositie.set(rij.position, rij)
    }

    const nieuw = bron.map((rij) => {
      const bestaand = eigenPerPositie.get(rij.position)
      // Het id van DEZE maat behouden; alleen de waarde komt van maat 0.
      return bestaand ? { ...bestaand, position: rij.position, value: rij.value } : { position: rij.position, value: rij.value }
    })

    schrijf(maten[i], ['wall', 'height'], nieuw)
  }

  return maten
}

// Vult een niveau aan met tombstones voor alles wat de bewerkte lijst niet meer
// noemt, en doet dat recursief voor de geneste lijsten.
function vulNiveauAan (origineel, bewerkt, kinderen) {
  const orig = (Array.isArray(origineel) ? origineel : []).filter((el) => el && el.id)
  const items = (Array.isArray(bewerkt) ? bewerkt : []).filter((el) => el !== null && el !== undefined)

  const origPerId = new Map(orig.map((el) => [el.id, el]))
  const genoemd = new Set()
  const uit = []

  for (const el of items) {
    const kopie = { ...el }
    if (el.id) genoemd.add(el.id)
    const bron = el.id ? origPerId.get(el.id) : undefined

    for (const kind of kinderen) {
      const lijst = lees(el, kind.pad)
      // Noemt de payload de geneste lijst niet, dan is erover niets gezegd en
      // mag hij ook geen tombstones krijgen.
      if (!Array.isArray(lijst)) continue
      schrijf(kopie, kind.pad, vulNiveauAan(bron ? lees(bron, kind.pad) : [], lijst, kind.kinderen))
    }

    uit.push(kopie)
  }

  for (const el of orig) {
    if (!genoemd.has(el.id)) uit.push({ id: el.id, _delete: true })
  }

  return uit
}

/**
 * Maakt van "weggehaald uit de lijst" een expliciete verwijdering.
 *
 * Vergelijkt de bewerkte maten met de stand zoals die geladen is en zet voor elk
 * element dat verdwenen is -- een maat, een muurhoogte, een breedte of een
 * wandvak -- een `{ id, _delete: true }` terug in de payload. Zonder dat komt de
 * rij bij de volgende lezing gewoon weer terug: ontbreken verwijdert sinds taak
 * 8e niets meer.
 *
 * Muteert geen van beide invoeren.
 */
export function metVerwijderingen (origineleMaten, bewerkteMaten) {
  return vulNiveauAan(origineleMaten, bewerkteMaten, NIVEAUS)
}
